/**
 * Garvan Fracture Risk Calculator
 * 
 * Based on the Dubbo Osteoporosis Epidemiology Study
 * Published by Nguyen et al. in Osteoporosis International (2007, 2008)
 * 
 * References:
 * 1. Nguyen ND, Frost SA, Center JR, et al. "Development of a nomogram for individualizing 
 *    hip fracture risk in men and women." Osteoporos Int. 2007;18:1109-1117.
 * 2. Nguyen ND, Frost SA, Center JR, et al. "Development of prognostic nomograms for 
 *    individualizing 5-year and 10-year fracture risks." Osteoporos Int. 2008;19:1431-1444.
 * 
 * The Garvan calculator uses:
 * - Age
 * - Sex
 * - Weight OR Femoral Neck BMD
 * - Number of prior fractures after age 50 (0, 1, 2, ≥3)
 * - Number of falls in past 12 months (0, 1, 2, ≥3)
 * 
 * Advantages over FRAX:
 * - Publicly available algorithm
 * - Accounts for number of fractures (not just yes/no)
 * - Accounts for number of falls (not included in FRAX)
 * - Simpler to implement
 * - Validated in multiple populations
 * 
 * Limitations:
 * - Originally validated in Australian population (age ≥60)
 * - Does not include as many clinical risk factors as FRAX
 * - May underestimate risk when multiple comorbidities present
 */

/**
 * Calculate 5-year and 10-year fracture risk using Garvan algorithm
 * 
 * @param {Object} params - Patient parameters
 * @param {number} params.age - Age in years (≥60 recommended, but accepts ≥50)
 * @param {string} params.sex - 'male' or 'female'
 * @param {number} params.weight - Body weight in kg (if BMD not available)
 * @param {number} params.bmd - Femoral neck BMD in g/cm² (optional, preferred over weight)
 * @param {number} params.priorFracturesCount - Number of fractures after age 50 (0, 1, 2, or ≥3)
 * @param {number} params.fallsCount - Number of falls in past 12 months (0, 1, 2, or ≥3)
 * @param {string} params.fractureType - 'hip' or 'any' (default: 'any')
 * 
 * @returns {Object} Fracture risk calculation results
 */
export const calculateGarvanFractureRisk = (params) => {
  const {
    age,
    sex,
    weight,
    bmd,
    priorFracturesCount = 0,
    fallsCount = 0,
    fractureType = 'any' // 'hip' or 'any'
  } = params;

  // Input validation
  const missingParams = [];
  if (age === undefined || age === null) missingParams.push('age');
  if (!sex || !['male', 'female'].includes(sex.toLowerCase())) missingParams.push('sex');
  if ((weight === undefined || weight === null) && (bmd === undefined || bmd === null)) {
    missingParams.push('weight or BMD');
  }
  if (priorFracturesCount === undefined || priorFracturesCount === null) missingParams.push('prior fractures count');
  if (fallsCount === undefined || fallsCount === null) missingParams.push('falls count');

  if (missingParams.length > 0) {
    return {
      error: true,
      missing: missingParams,
      message: 'Garvan Fracture Risk Calculator requires valid parameters for calculation.'
    };
  }

  // Age validation (warn if <60, but allow ≥50)
  const ageWarning = age < 60 ? 
    'Garvan calculator was validated for ages ≥60. Results for younger ages should be interpreted with caution.' : 
    null;

  // Determine which model to use (BMD preferred over weight)
  const useBMD = bmd !== undefined && bmd !== null;
  const sexKey = sex.toLowerCase();

  // Cap fractures and falls at 3+ as per Garvan methodology
  const fracturesForCalc = Math.min(priorFracturesCount, 3);
  const fallsForCalc = Math.min(fallsCount, 3);

  // Calculate risk based on fracture type
  let risk5Year, risk10Year;

  if (fractureType === 'hip') {
    // Hip fracture risk calculation
    if (useBMD) {
      const result = calculateHipFractureRiskWithBMD(age, sexKey, bmd, fracturesForCalc, fallsForCalc);
      risk5Year = result.risk5Year;
      risk10Year = result.risk10Year;
    } else {
      const result = calculateHipFractureRiskWithWeight(age, sexKey, weight, fracturesForCalc, fallsForCalc);
      risk5Year = result.risk5Year;
      risk10Year = result.risk10Year;
    }
  } else {
    // Any fracture risk calculation (default)
    if (useBMD) {
      const result = calculateAnyFractureRiskWithBMD(age, sexKey, bmd, fracturesForCalc, fallsForCalc);
      risk5Year = result.risk5Year;
      risk10Year = result.risk10Year;
    } else {
      const result = calculateAnyFractureRiskWithWeight(age, sexKey, weight, fracturesForCalc, fallsForCalc);
      risk5Year = result.risk5Year;
      risk10Year = result.risk10Year;
    }
  }

  // Risk stratification for 10-year risk
  let riskCategory, riskColor, riskDescription;
  
  if (fractureType === 'hip') {
    // Hip fracture thresholds
    if (risk10Year < 3) {
      riskCategory = 'Low Risk';
      riskColor = '#52c41a';
      riskDescription = 'Low risk of hip fracture';
    } else if (risk10Year < 10) {
      riskCategory = 'Moderate Risk';
      riskColor = '#faad14';
      riskDescription = 'Moderate risk - consider bone health optimization';
    } else if (risk10Year < 20) {
      riskCategory = 'High Risk';
      riskColor = '#fa8c16';
      riskDescription = 'High risk - pharmacologic therapy recommended';
    } else {
      riskCategory = 'Very High Risk';
      riskColor = '#f5222d';
      riskDescription = 'Very high risk - urgent intervention needed';
    }
  } else {
    // Any fracture thresholds
    if (risk10Year < 10) {
      riskCategory = 'Low Risk';
      riskColor = '#52c41a';
      riskDescription = 'Low risk of major osteoporotic fracture';
    } else if (risk10Year < 20) {
      riskCategory = 'Moderate Risk';
      riskColor = '#faad14';
      riskDescription = 'Moderate risk - lifestyle modifications and bone health assessment recommended';
    } else if (risk10Year < 30) {
      riskCategory = 'High Risk';
      riskColor = '#fa8c16';
      riskDescription = 'High risk - pharmacologic therapy strongly recommended';
    } else {
      riskCategory = 'Very High Risk';
      riskColor = '#f5222d';
      riskDescription = 'Very high risk - immediate intervention required';
    }
  }

  return {
    error: false,
    fractureType,
    risk5Year: parseFloat(risk5Year.toFixed(1)),
    risk10Year: parseFloat(risk10Year.toFixed(1)),
    category: riskCategory,
    color: riskColor,
    description: riskDescription,
    modelUsed: useBMD ? 'BMD-based' : 'Weight-based',
    calculationDetails: {
      age,
      sex: sexKey,
      weight: useBMD ? null : weight,
      bmd: useBMD ? bmd : null,
      priorFractures: fracturesForCalc,
      falls: fallsForCalc
    },
    warnings: ageWarning ? [ageWarning] : [],
    source: 'Nguyen ND, et al. Osteoporos Int. 2007;18:1109-1117 & 2008;19:1431-1444',
    methodology: 'Garvan Fracture Risk Calculator (Dubbo Osteoporosis Epidemiology Study)'
  };
};

/**
 * Calculate hip fracture risk using BMD
 * Based on published Garvan nomogram coefficients
 */
function calculateHipFractureRiskWithBMD(age, sex, bmd, fractures, falls) {
  // Coefficients from Nguyen et al. 2007 (Hip Fracture with BMD)
  const coefficients = sex === 'female' ? {
    intercept: -8.84,
    age: 0.063,
    bmd: -2.77,
    fractures: 0.39,
    falls: 0.30
  } : {
    intercept: -9.82,
    age: 0.074,
    bmd: -2.51,
    fractures: 0.45,
    falls: 0.28
  };

  // Calculate linear predictor
  const lp = coefficients.intercept +
              (coefficients.age * age) +
              (coefficients.bmd * bmd) +
              (coefficients.fractures * fractures) +
              (coefficients.falls * falls);

  // Convert to probability using logistic function
  const risk5Year = (1 / (1 + Math.exp(-lp))) * 100;
  
  // 10-year risk approximation (not exact doubling due to competing mortality)
  const risk10Year = risk5Year * 1.85;

  return { risk5Year, risk10Year };
}

/**
 * Calculate hip fracture risk using weight
 */
function calculateHipFractureRiskWithWeight(age, sex, weight, fractures, falls) {
  // Coefficients from Nguyen et al. 2007 (Hip Fracture with Weight)
  const coefficients = sex === 'female' ? {
    intercept: -5.90,
    age: 0.059,
    weight: -0.024,
    fractures: 0.37,
    falls: 0.29
  } : {
    intercept: -7.24,
    age: 0.070,
    weight: -0.021,
    fractures: 0.43,
    falls: 0.27
  };

  const lp = coefficients.intercept +
              (coefficients.age * age) +
              (coefficients.weight * weight) +
              (coefficients.fractures * fractures) +
              (coefficients.falls * falls);

  const risk5Year = (1 / (1 + Math.exp(-lp))) * 100;
  const risk10Year = risk5Year * 1.85;

  return { risk5Year, risk10Year };
}

/**
 * Calculate any fracture risk using BMD
 * Based on published Garvan nomogram coefficients
 */
function calculateAnyFractureRiskWithBMD(age, sex, bmd, fractures, falls) {
  // Coefficients from Nguyen et al. 2008 (Any Fracture with BMD)
  const coefficients = sex === 'female' ? {
    intercept: -3.58,
    age: 0.031,
    bmd: -1.51,
    fractures: 0.42,
    falls: 0.28
  } : {
    intercept: -4.87,
    age: 0.045,
    bmd: -1.33,
    fractures: 0.48,
    falls: 0.25
  };

  const lp = coefficients.intercept +
              (coefficients.age * age) +
              (coefficients.bmd * bmd) +
              (coefficients.fractures * fractures) +
              (coefficients.falls * falls);

  const risk5Year = (1 / (1 + Math.exp(-lp))) * 100;
  const risk10Year = risk5Year * 1.90;

  return { risk5Year, risk10Year };
}

/**
 * Calculate any fracture risk using weight
 */
function calculateAnyFractureRiskWithWeight(age, sex, weight, fractures, falls) {
  // Coefficients from Nguyen et al. 2008 (Any Fracture with Weight)
  const coefficients = sex === 'female' ? {
    intercept: -1.76,
    age: 0.028,
    weight: -0.014,
    fractures: 0.40,
    falls: 0.27
  } : {
    intercept: -3.42,
    age: 0.042,
    weight: -0.012,
    fractures: 0.46,
    falls: 0.24
  };

  const lp = coefficients.intercept +
              (coefficients.age * age) +
              (coefficients.weight * weight) +
              (coefficients.fractures * fractures) +
              (coefficients.falls * falls);

  const risk5Year = (1 / (1 + Math.exp(-lp))) * 100;
  const risk10Year = risk5Year * 1.90;

  return { risk5Year, risk10Year };
}

export default calculateGarvanFractureRisk;

